import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Tenant settings come inline with /auth/me and /auth/login responses,
  // so we derive them from the user object — no extra request, no race conditions.
  const tenantSettings = user
    ? {
        default_currency: user.default_currency || "TRY",
        has_demo_data: !!user.has_demo_data,
        default_warehouse_id: user.default_warehouse_id || null,
        default_stock_warehouse_id: user.default_stock_warehouse_id || null,
        sales_stock_enabled: !!user.sales_stock_enabled,
      }
    : null;
  const defaultCurrency = (user && user.default_currency) || "TRY";
  const defaultWarehouseId = (user && user.default_warehouse_id) || null;
  // Stok/Satış için varsayılan depo — belirlenmemişse backend emanet
  // varsayılanına düşürerek gönderir (şartname #9).
  const defaultStockWarehouseId =
    (user && (user.default_stock_warehouse_id || user.default_warehouse_id)) || null;
  // Paket bazlı modül erişimi (şartname #10): pasifse menü gösterilmez.
  const salesStockEnabled = !!(user && user.sales_stock_enabled);
  // Global bakım modu — /auth/me ve /auth/login ile birlikte gelir. Yalnızca
  // UYARI göstermek için kullanılır; engelleme backend'de (maintenance.py).
  const maintenanceMode = !!(user && user.maintenance_mode);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, recaptchaToken) => {
    const { data } = await api.post("/auth/login", {
      email,
      password,
      recaptcha_token: recaptchaToken || null,
    });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  const updateTenantSettings = async (patch) => {
    const { data } = await api.put("/me/tenant-settings", patch);
    // Merge updated tenant settings back into user object
    setUser((prev) => (prev ? { ...prev, ...data } : prev));
    return data;
  };

  const refreshTenantSettings = refresh;

  const isMaster = user && user.role === "master";
  const isOwner = user && user.role === "owner";
  const isPersonel = user && user.role === "personel";
  const canManage = isOwner || isMaster;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        refresh,
        tenantSettings,
        defaultCurrency,
        defaultWarehouseId,
        defaultStockWarehouseId,
        salesStockEnabled,
        maintenanceMode,
        updateTenantSettings,
        refreshTenantSettings,
        isMaster,
        isOwner,
        isPersonel,
        canManage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
